<template>
  <ModalLayout small title="Unarchive">
    <div class="rounded-t-[10px] bg-white px-[24px] pt-[41px] sm:px-[40px]">
      <div
        class="absolute right-[24px] top-[36px] flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-full border border-gpx_gray-800 sm:right-[40px]"
        @click.prevent="close"
      >
        <XMarkIcon
          class="heroicon-stroke-w-1.4 h-[20px] w-[20px] text-gpx_gray-200"
          @click="close"
        />
      </div>
      <div class="border-b border-b-gpx_gray-800 pb-[18px]">
        <div class="text-gpx_black-100 text-2xl font-semibold">
          Unarchive {{ title }}
        </div>
      </div>
      <div class="mt-[25px] min-h-[60px] text-sm text-gpx_gray-100">
        <div v-if="loading" class="flex items-center justify-center py-4">
          <Spinner />
        </div>
        <p v-else-if="preflightError" class="text-red-600">
          {{ preflightError }}
        </p>
        <template v-else>
          {{ bodyText }}
        </template>
      </div>
    </div>
    <div
      class="flex w-full gap-x-[16px] rounded-b-[10px] bg-white px-[24px] py-[32px] sm:px-[40px]"
    >
      <Button class="w-full" @click="close">
        Cancel
      </Button>
      <Button
        ref="unarchiveButtonRef"
        variant="destructive"
        class="w-full"
        :disabled="!canConfirm"
        :loading="isSubmitting"
        @click="handleConfirm"
      >
        Unarchive
      </Button>
    </div>
  </ModalLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import { useToast } from 'vue-toastification';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import ModalLayout from '@/layouts/ModalLayout.vue';
import Spinner from '@/components/_common/Spinner.vue';
import { useDevice } from '@/composables/useDevice';

const props = defineProps<{
  params: {
    accountId: number;
    deviceIds: number[];
    onSuccess: () => void;
  };
}>();

const emit = defineEmits<{ (e: 'close'): void }>();

const toast = useToast();
const deviceStore = useDevice();

const loading = ref(true);
const isSubmitting = ref(false);
const restorable = ref<number[]>([]);
const past10Count = ref(0);
const bodyText = ref('');
const preflightError = ref('');
const unarchiveButtonRef = ref<{ $el?: HTMLButtonElement } | null>(null);
const title = computed(() => {
  const n =
    restorable.value.length > 0
      ? restorable.value.length
      : props.params.deviceIds.length;
  return `${n} tracker${n === 1 ? '' : 's'}`;
});

const canConfirm = computed(() => !loading.value && restorable.value.length > 0);

function close() {
  emit('close');
}

async function runPreflight() {
  loading.value = true;
  bodyText.value = '';
  preflightError.value = '';
  restorable.value = [];
  past10Count.value = 0;
  try {
    const result = await deviceStore.checkUnarchiveEligibility(
      props.params.accountId,
      props.params.deviceIds,
    );
    restorable.value = result.restorable ?? [];
    const notRestorable = result.not_restorable ?? [];
    past10Count.value = notRestorable.filter((n) => n.reason === 'past_10_days').length;

    if (restorable.value.length === 0) {
      bodyText.value =
        past10Count.value > 0
          ? 'None of the selected trackers can be restored (past 10-day limit).'
          : 'Failed to check which trackers can be restored.';
      return;
    }

    const total = props.params.deviceIds.length;
    if (past10Count.value > 0) {
      bodyText.value = `${restorable.value.length} of ${total} selected can be restored. ${past10Count.value} are past the 10-day limit. Unarchive ${restorable.value.length}?`;
    } else {
      bodyText.value = 'Are you sure you want to unarchive the selected trackers?';
    }
  } catch {
    preflightError.value = 'Failed to check which trackers can be restored. Please try again or contact support.';
  } finally {
    loading.value = false;
  }
  if (restorable.value.length > 0) {
    nextTick(() => {
      (unarchiveButtonRef.value as { $el?: HTMLButtonElement } | null)?.$el?.focus();
    });
  }
}

async function handleConfirm() {
  if (!canConfirm.value || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    await deviceStore.updateDevicesVisibility(
      props.params.accountId,
      restorable.value,
      'active',
    );
    props.params.onSuccess();
    close();
    const message =
      past10Count.value > 0
        ? `${restorable.value.length} trackers unarchived. ${past10Count.value} could not be restored (past 10-day limit).`
        : 'Trackers unarchived';
    toast.success(message);
  } catch {
    toast.error('Failed to unarchive. Some may be past the 10-day recovery period.');
  } finally {
    isSubmitting.value = false;
  }
}

onMounted(() => {
  runPreflight();
});
</script>
